import { interfaces } from 'inversify';
import React from 'react';
import { Resolver } from '../../app/Container';
import { CommandWizard } from './CommandWizard';

export interface CommandContext {
    resolve<T>(identifier: interfaces.ServiceIdentifier<T>, strict?: boolean): T;
    call<T>(identifier: interfaces.ServiceIdentifier<T>, strict?: boolean): T;
    activate<T>(identifier: interfaces.Newable<T>): T;
    dispatch<P>(def: CommandDefinition<P>, input?: P): unknown;
}

export type CommandSignature<P> = (cc: CommandContext, input: P) => void;

export type CommandFlowActionCallback = (action: 'complete' | 'cancel') => void;
export type CommandFlowComponent = React.ComponentType<{
    onAction: CommandFlowActionCallback,
}>;

/**
 * Defines the interface of an object that defines a command.  Commands have a system friendly name and human friendly
 * label, an implementation method and an optional wizard.  The interface is generic and should be typed with the
 * required parameter object of the implementation callback.  Using the `defineCommand` function is the easiest way
 * to generate a command definition.
 *
 * Commands may be invoked directly by a user or called by user or app code.  In order to be compatible with invocation
 * by users, commands must provide a wizard, even if they require no parameters.  The wizard indicates to the system
 * how to gather the parameter information from the user.  Pass an empty array if the command requires no parameters
 * and should be invocable by users.
 */
export interface CommandDefinition<P = void> {
    name: string;
    label?: string;
    impl: CommandSignature<P>;
    wizard?: CommandWizard;
    ui?: {
        context?: {
            label: string,
            when: (r: Resolver) => boolean,
        },
        flow?: CommandFlowComponent,
    };
}

/**
 * Creates a command definition for a new command type.
 *
 * @param name the commands system friendly name.
 * @param label the commands human friendly label.
 * @param impl the command implementation.
 * @param wizard the optional command palette wizard.
 */
export const defineCommand = <P>(name: string, label: string, impl: CommandSignature<P>, wizard: CommandWizard = []) => {
    return ({ name, label, impl, wizard }) as CommandDefinition<P>;
};

export const DefineCommand = <P>(def: CommandDefinition<P>) => {
    return def;
};

export const bindCommand = <P>(def: CommandDefinition<P>, input: P) => {
    return defineCommand(
        `bound:${def.name}`,
        null,
        cc => cc.dispatch(def, input),
        []
    );
};
